/* eslint-disable */
import React, { useContext, useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'

import Input from 'components/Input'
import Textarea from 'components/Textarea'
import { requestUpdateSiteSpecieAssesmentParameter } from 'api/siteSpecieAssesmentParameter'
import { siteSpecieDerivationOfEstimate } from 'constants/siteSpecieDerivationOfEstimate'
import Select from 'components/Select'
import { delay } from 'utils/delay'
import { isValidDecimalNumber, noZeroAfterDecimal } from 'utils/isValidDecimalNumber'
import { isValidYYYYFormat } from 'validators/isValidYYYYFormat'
import { SiteContext } from 'providers/site'
import { ProfileContext } from 'providers/profile'
import { defaultSiteProposalValidationResults } from 'constants/defaultSiteProposalValidationResults'
import {
  SITE_STATUS_DRAFT_ID,
  SITE_STATUS_NOMINATED_ID,
  SITE_STATUS_PROPOSED_ID,
  SITE_STATUS_REVIEWED_BY_RFP_ID
} from 'constants/siteStatuses'
import { canViewSensitiveSpeciesAtSite } from 'utils/rolePermissions'
import capitalizeFirstLetterString from 'utils/capitalizeFirstLetter'
import MultiSelectionDropdown from 'components/MultiSelectDropdown'
import MultiC2C3SelectionDropdown from 'components/MultiSelectDropdown/multi'
import { minBestMaxValidation, minMaxValidation } from 'utils/minBestMaxValidation'
import { NUMBER_OF_MATURE_INDIVIDUALS_ID, NUMBER_OF_LOCALITIES_ID } from 'constants/speciesParameterType'
import checkIsNumber from 'utils/checkIsNumber'
import {
  B_Value,
  B2_Value,
  B3_Value,
  B3C_Value,
  D1a_Value,
  D1b_Value,
  D2_Value,
  D3_Value,
  IBA_B1b_Value,
  IBA_B2b_Value,
  IBA_C2_Value,
  IBA_C3_Value,
  IBA_C6_Value,
  getIdCriteria,
  arrIdCriteria,
  arrIdIbaCriteria
} from './constants'
import { useIBAAccess } from 'hooks/useIBAAccess'
import {
  SpeciesDetailsContainer,
  SpeciesDetailsItemWrapper,
  FullWidthSpecieEstimateItemWrapper,
  SpeciesDetailsIBAItemWrapper
} from '../style'

const formattedDerivationOptions = siteSpecieDerivationOfEstimate.map((item) => {
  return {
    value: item.id,
    label: capitalizeFirstLetterString(item.name)
  }
})

const criteriaDefaultOptions = [
  { label: 'Site regularly holds trigger in one or more life cycle stages (A1, B1)', value: 1 },
  {
    label: 'Species is regularly occurring and is range-restricted in this season (B2)',
    value: 2
  },
  {
    label:
      'Site regularly holds trigger in one or more life cycle stages, and is ecoregion or bioregion-restricted (B3a, B3b)',
    value: 3
  },
  { label: 'Site part of the globally most important 5% of occupied habitat (B3c)', value: 4 },
  { label: 'Site predictably holds aggregation in one or more life cycle stages (D1a)', value: 5 },
  {
    label: 'Site predictably holds one of 10 largest aggregations known for the species (D1b)',
    value: 6
  },
  {
    label: 'Site supports ≥10% of the global population as a refugium during periods of environmental stress (D2)',
    value: 7
  },
  {
    label: 'Site predictably acts as recruitment source maintaining ≥10% of the global population (D3)',
    value: 8
  }
]

const criteriaDefaultNoD1Options = [
  { label: 'Site regularly holds trigger in one or more life cycle stages (A1, B1)', value: 1 },
  {
    label: 'Species is regularly occurring and is range-restricted in this season (B2)',
    value: 2
  },
  {
    label:
      'Site regularly holds trigger in one or more life cycle stages, and is ecoregion or bioregion-restricted (B3a, B3b)',
    value: 3
  },
  { label: 'Site part of the globally most important 5% of occupied habitat (B3c)', value: 4 },
  {
    label: 'Site supports ≥10% of the global population as a refugium during periods of environmental stress (D2)',
    value: 7
  },
  {
    label: 'Site predictably acts as recruitment source maintaining ≥10% of the global population (D3)',
    value: 8
  }
]

const ibaCriteriaEUDefaultOptions = [
  {
    label:
      'Species has unfavourable conservation status in the region, and site is one of the top ‘n’ most important sites in the country (IBA B1b)',
    value: 1
  },
  {
    label:
      'Species population is concentrated in the region, and site is one of the top ‘n’ most important sites in the country (IBA B2a)',
    value: 2
  },
  { label: 'Species is listed on Annex I of EU Birds Directive (IBA C2)', value: 3 },
  { label: 'Species is migratory (IBA C3)', value: 4 },
  {
    label:
      'Species is listed on Annex I of EU Birds Directive, and site is one of the top five most important sites in the NUTS region (IBA C6)',
    value: 5
  }
]

const ibaCriteriaDefaultOptions = [
  {
    label:
      'Species has unfavourable conservation status in the region, and site is one of the top ‘n’ most important sites in the country (IBA B1b)',
    value: 1
  },
  {
    label:
      'Species population is concentrated in the region, and site is one of the top ‘n’ most important sites in the country (IBA B2a)',
    value: 2
  },
  {
    label:
      'Species is listed on Annex I of EU Birds Directive, and site is one of the top five most important sites in the NUTS region (IBA C6)',
    value: 5
  }
]

// Function to get dynamic IBA criteria options for EU countries with mutual exclusivity
const getIbaCriteriaEUOptions = (selectedValues) => {
  const includesC2 = selectedValues.includes(IBA_C2_Value)
  const includesC3 = selectedValues.includes(IBA_C3_Value)

  return ibaCriteriaEUDefaultOptions.map((option) => {
    if (option.value === IBA_C2_Value && includesC3) {
      return { ...option, disabled: true }
    }
    if (option.value === IBA_C3_Value && includesC2) {
      return { ...option, disabled: true }
    }
    return { ...option, disabled: false }
  })
}

const fields = {
  min: 'minSite',
  best: 'bestSite',
  max: 'maxSite'
}

const yearFields = {
  min: 'minYear',
  best: 'best',
  max: 'maxYear'
}

const inputSaveDelayTime = 300
const updateSourceTimeOut = delay(inputSaveDelayTime)
const updateDerivationTimeOut = delay(inputSaveDelayTime)
// const updateYearTimeOut = delay(inputSaveDelayTime)
const updateNotesTimeOut = delay(inputSaveDelayTime)
const updateCriteriaTimeOut = delay(inputSaveDelayTime)
const updateJustificationTimeOut = delay(inputSaveDelayTime)
const updateSourceHabitatTimeOut = delay(inputSaveDelayTime)
const updateEvidenceTimeOut = delay(inputSaveDelayTime)
const updateYearRecentTimeOut = delay(inputSaveDelayTime)
const updateMinNumberTimeOut = delay(inputSaveDelayTime)
const updateWhatAre10RuTimeOut = delay(inputSaveDelayTime)
const updateSourceReproducTimeOut = delay(inputSaveDelayTime)
const SpeciesParameterSiteSpeciesDataContent = ({
  parameter,
  siteSpecieParameterId,
  updateParameterList,
  species,
  qualified,
  assessmentParameter,
  siteBestGlobal,
  setSiteBestGlobal,
  siteNatureOfOccurance,
  setSiteNatureOfOccurance,
  siteJustification,
  setSiteJustification
}) => {
  const params = useParams()
  const { site, siteProposalFieldValidation, isEditable } = useContext(SiteContext)
  const { profile } = useContext(ProfileContext)
  const disabled =
    !parameter?.SiteAssesmentParameterTypeId || site.SiteStatusId !== SITE_STATUS_DRAFT_ID || !isEditable || !qualified
  // Check if user has IBA access for birds for this site
  const { hasSiteIBAAccessForBirds } = useIBAAccess()
  
  // Simplified logic: only check if user has IBA access for birds for this site and species is AVES.
  // Individual proposers/co-proposers can see sensitive species when visibility has been approved for this site.
  let showBirdsDetails =
    species.className === 'AVES' &&
    hasSiteIBAAccessForBirds(site, species.className) ||
    (site.SiteStatusId === SITE_STATUS_PROPOSED_ID ||
      site.SiteStatusId === SITE_STATUS_NOMINATED_ID ||
      site.SiteStatusId === SITE_STATUS_REVIEWED_BY_RFP_ID ||
      canViewSensitiveSpeciesAtSite(site, profile.RoleId))

  const [minSite, setMinSite] = useState(parameter.minSite || '')
  const [bestSite, setBestSite] = useState(parameter.bestSite || '')
  const [maxSite, setMaxSite] = useState(parameter.maxSite || '')
  const [source, setSource] = useState(parameter.sourceOfData || '')
  const [derivation, setDerivation] = useState(
    formattedDerivationOptions.find((item) => {
      return item.value === parameter.DerivationOfEstimateId
    }) || null
  )
  const [notes, setNotes] = useState(parameter.notesOnSiteDataForSpecies || '')
  const [natureOfOccurrence, setNatureOfOccurrence] = useState(getIdCriteria(parameter, arrIdCriteria))
  const [ibaConditions, setIbaConditions] = useState(getIdCriteria(parameter, arrIdIbaCriteria))
  // Local state for real-time IBA selection changes (before saving)
  const [currentIbaSelection, setCurrentIbaSelection] = useState(getIdCriteria(parameter, arrIdIbaCriteria))
  const [justification, setJustification] = useState(parameter.justification || '')
  const [sourceHabitat, setSourceHabitat] = useState(parameter.globallyMostImportant5 || '')
  const [minYear, setMinYear] = useState(parameter.earliestDataSetYear ? parameter.earliestDataSetYear : '')
  const [maxYear, setMaxYear] = useState(parameter.latestDataSetYear ? parameter.latestDataSetYear : '')
  const [error, setError] = useState({ field: '', error: '' })
  const [yearError, setYearError] = useState({ field: '', error: '' })
  const [evidence, setEvidence] = useState(parameter.speciesPresenceEvidence || '')
  const [yearRecent, setYearRecent] = useState(parameter.yearPresenceEvidence || '')
  const [unitsNumber, setUnitsNumber] = useState(parameter.minReproductiveUnits || '')
  const [comprised, setComprised] = useState(parameter.ruCompromisedOf || '')
  const [sourceReproduc, setSourceReproduc] = useState(parameter.ruSource || '')

  const checkFields = siteProposalFieldValidation.assessmentParameters[parameter.id]
    ? siteProposalFieldValidation.assessmentParameters[parameter.id]
    : defaultSiteProposalValidationResults.assessmentParameters

  const ibaFields = Object.keys(parameter)
  if (
    !ibaFields.includes(
      'minRegional',
      'bestRegional',
      'maxRegional',
      'sourceRegionalData',
      'sourceOfEu',
      'euPopulation',
      'nationalPopulation',
      'sourceOfNational'
    )
  ) {
    showBirdsDetails = false
  }

  // Synchronize currentIbaSelection with ibaConditions when parameter changes
  useEffect(() => {
    setCurrentIbaSelection(ibaConditions)
  }, [ibaConditions])

  // Handle real-time IBA selection changes for immediate UI feedback
  const handleIbaSelectionChange = (newSelection) => {
    // Enforce mutual exclusivity between C2 and C3 in real-time
    const includesC2 = newSelection.includes(IBA_C2_Value)
    const includesC3 = newSelection.includes(IBA_C3_Value)

    let cleanedSelection = [...newSelection]
    if (includesC2 && includesC3) {
      // Keep only the last selected one
      const lastSelected = newSelection[newSelection.length - 1]
      if (lastSelected === IBA_C2_Value) {
        cleanedSelection = newSelection.filter((val) => val !== IBA_C3_Value)
      } else {
        cleanedSelection = newSelection.filter((val) => val !== IBA_C2_Value)
      }
    }

    // Update local state immediately for real-time UI feedback
    setCurrentIbaSelection(cleanedSelection)
  }

  function handleChangeCriteria(id) {
    setNatureOfOccurrence(id)

    updateCriteriaTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          regularlyHeld: id.some((element) => {
            return element === B_Value
          }),
          regularlyHeldRestricted: id.some((element) => {
            return element === B3_Value
          }),
          aggregationPredictability: id.some((element) => {
            return element === D1a_Value
          }),
          aggregationsAsCriteria: id.some((element) => {
            return element === D1b_Value
          }),
          refugium: id.some((element) => {
            return element === D2_Value
          }),
          recruitmentSource: id.some((element) => {
            return element === D3_Value
          }),
          regularlyOccurring: id.some((element) => {
            return element === B2_Value
          }),
          globallyMostImportant: id.some((element) => {
            return element === B3C_Value
          })
        }
      ).then((data) => {
        if (id.includes(D1b_Value) && !id.includes(D1a_Value)) {
          setSiteNatureOfOccurance(true)
          setSiteJustification(false)
        }

        if (id.includes(D1a_Value) && !id.includes(D1b_Value)) {
          setSiteNatureOfOccurance(false)
          setSiteJustification(false)
        }

        if (!id.includes(D1a_Value) && !id.includes(D1b_Value)) {
          setSiteNatureOfOccurance(false)
          setSiteJustification(true)
        }

        if (
          (id.includes(B2_Value) || id.includes(D2_Value) || id.includes(D3_Value)) &&
          assessmentParameter.value == NUMBER_OF_MATURE_INDIVIDUALS_ID
        ) {
          setSiteJustification(false)
        }

        if (id.length == 0) {
          setSiteNatureOfOccurance(!siteNatureOfOccurance)
          setSiteJustification(!siteJustification)
        }
        updateParameterList(data)
      })
    })
  }

  function handleChangeIbaCriteria(id) {
    // Enforce mutual exclusivity between C2 and C3
    const includesC2 = id.includes(IBA_C2_Value)
    const includesC3 = id.includes(IBA_C3_Value)

    let cleanedId = [...id]
    if (includesC2 && includesC3) {
      // Keep only the last selected one
      const lastSelected = id[id.length - 1]
      if (lastSelected === IBA_C2_Value) {
        cleanedId = id.filter((val) => val !== IBA_C3_Value)
      } else {
        cleanedId = id.filter((val) => val !== IBA_C2_Value)
      }
    }

    setIbaConditions(cleanedId)
    setCurrentIbaSelection(cleanedId)

    updateCriteriaTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          unfavourable: cleanedId.includes(IBA_B1b_Value),
          concentrated: cleanedId.includes(IBA_B2b_Value),
          annexListed: cleanedId.includes(IBA_C2_Value),
          migratory: cleanedId.includes(IBA_C3_Value),
          annexListedMi: cleanedId.includes(IBA_C6_Value)
        }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeYearRecent(e) {
    // eslint-disable-next-line no-magic-numbers
    if (Number.isInteger(Number(e.target.value)) && e.target.value.length < 5) {
      setYearRecent(e.target.value)
    }

    if (isValidYYYYFormat(Number(e.target.value)) || e.target.value === '') {
      updateYearRecentTimeOut(() => {
        const yearPresenceEvidence = e.target.value === '' ? '' : e.target.value
        requestUpdateSiteSpecieAssesmentParameter(
          siteSpecieParameterId,
          params.siteHistoryId,
          species.id,
          species.historyId,
          { yearPresenceEvidence }
        ).then((data) => {
          updateParameterList(data)
        })
      })
    }
  }

  function handleChangeUnitsNumber(e) {
    if (checkIsNumber(e.target.value)) {
      setUnitsNumber(e.target.value)

      updateMinNumberTimeOut(() => {
        requestUpdateSiteSpecieAssesmentParameter(
          siteSpecieParameterId,
          params.siteHistoryId,
          species.id,
          species.historyId,
          { minReproductiveUnits: e.target.value === '' ? 0 : e.target.value }
        ).then((data) => {
          updateParameterList(data)
        })
      })
    }
  }

  function handleChangeComprised(e) {
    setComprised(e.target.value)

    updateWhatAre10RuTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        { ruCompromisedOf: e.target.value }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeSourceReproduc(e) {
    setSourceReproduc(e.target.value)

    updateSourceReproducTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        { ruSource: e.target.value }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeEvidence(e) {
    setEvidence(e.target.value)

    updateEvidenceTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        { speciesPresenceEvidence: e.target.value }
      ).then((data) => {
        // Evidence updated successfully
      })
    })
  }

  function handleChangeJustification(e) {
    setJustification(e.target.value)

    updateJustificationTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          justification: e.target.value === '' ? null : e.target.value
        }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeSourceHabitat(e) {
    setSourceHabitat(e.target.value)

    updateSourceHabitatTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          globallyMostImportant5: e.target.value === '' ? null : e.target.value
        }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeMinYear(e) {
    // Allow empty or valid 4-digit year
    if (
      Number.isInteger(Number(e.target.value)) &&
      e.target.value.length < 5 &&
      e.target.value <= new Date().getFullYear()
    ) {
      setMinYear(e.target.value)
    }

    if (isValidYYYYFormat(Number(e.target.value)) || e.target.value === '') {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          earliestDataSetYear: e.target.value === '' ? null : Number(e.target.value),
          latestDataSetYear: maxYear === '' ? null : Number(maxYear)
        }
      ).then((data) => {
        updateParameterList(data)
        setYearError({ field: '', error: '' })
      })
    }
  }

  function handleChangeMaxYear(e) {
    const input = e.target.value

    if (/^\d{0,4}$/.test(input)) {
      setMaxYear(input)

      if (input === '') {
        setYearError({ field: '', error: '' })
        return
      }

      if (input.length === 4) {
        const yearNum = Number(input)
        const currentYear = new Date().getFullYear()

        if (yearNum <= currentYear) {
          setYearError({ field: '', error: '' })

          requestUpdateSiteSpecieAssesmentParameter(
            siteSpecieParameterId,
            params.siteHistoryId,
            species.id,
            species.historyId,
            {
              earliestDataSetYear: minYear === '' ? null : Number(minYear),
              latestDataSetYear: yearNum
            }
          ).then(updateParameterList)
        } else {
          setYearError({ field: 'maxYear', error: 'Year cannot be in the future' })
        }
      }
    }
  }

  function handleChangeMinSite(e) {
    const errorMessage = minBestMaxValidation(e.target.value, bestSite, maxSite, fields, 'min')

    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setMinSite(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }

    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMinSite(e.target.value)
        setError(errorMessage)
      }
      let value = e.target.value
      if (
        (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
          parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
        e.target.value.includes('.')
      ) {
        value = Number(parseFloat(e.target.value).toFixed('0'))
      } else {
        value = Number(parseFloat(e.target.value).toFixed('4'))
      }
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          minSite: e.target.value ? value : null
        }
      ).then((data) => {
        updateParameterList(data)
      })
    }
  }

  function handleBlurMinSite(e) {
    if (
      (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
        parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
      e.target.value.includes('.')
    ) {
      return
    }

    const errorMessage = minBestMaxValidation(e.target.value, bestSite, maxSite, fields, 'min')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setMinSite(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMinSite(e.target.value)
        setError(errorMessage)
      }
      const value = Number(parseFloat(e.target.value).toFixed('10'))
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          minSite: e.target.value ? value : null
        }
      ).then((data) => {
        updateParameterList(data)
      })
    }
  }

  function handleChangeBestSite(e) {
    const errorMessage = minBestMaxValidation(minSite, e.target.value, maxSite, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setBestSite(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setBestSite(e.target.value)
        setError(errorMessage)
      }
      let value = e.target.value

      if (
        (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
          parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
        e.target.value.includes('.')
      ) {
        value = Number(parseFloat(e.target.value).toFixed('0'))
      } else {
        value = Number(parseFloat(e.target.value).toFixed('4'))
      }
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          bestSite: e.target.value ? value : null
        }
      ).then((data) => {
        updateParameterList(data)
      })
    }
  }

  function handleBlurBestSite(e) {
    if (
      (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
        parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
      e.target.value.includes('.')
    ) {
      return
    }

    const errorMessage = minBestMaxValidation(minSite, e.target.value, maxSite, fields, 'best')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setBestSite(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setBestSite(e.target.value)
        setError(errorMessage)
      }
      const value = Number(parseFloat(e.target.value).toFixed('4'))
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          bestSite: e.target.value ? value : null
        }
      ).then((data) => {
        updateParameterList(data)
      })
    }
  }

  function handleChangeMaxSite(e) {
    const errorMessage = minBestMaxValidation(minSite, bestSite, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setMaxSite(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMaxSite(e.target.value)
        setError(errorMessage)
      }
      let value = e.target.value
      if (
        (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
          parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
        e.target.value.includes('.')
      ) {
        value = Number(parseFloat(e.target.value).toFixed('0'))
      } else {
        value = Number(parseFloat(e.target.value).toFixed('4'))
      }
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          maxSite: e.target.value ? value : null
        }
      ).then((data) => {
        updateParameterList(data)
      })
    }
  }

  function handleBlurMaxSite(e) {
    if (
      (parameter.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID ||
        parameter.SiteAssesmentParameterTypeId === NUMBER_OF_LOCALITIES_ID) &&
      e.target.value.includes('.')
    ) {
      return
    }

    const errorMessage = minBestMaxValidation(minSite, bestSite, e.target.value, fields, 'max')
    if (errorMessage.field !== '') {
      if (isValidDecimalNumber(Number(e.target.value))) {
        if (noZeroAfterDecimal(e.target.value)) {
          setMaxSite(e.target.value)
          setError(errorMessage)
        }
      }
      return
    }
    if (isValidDecimalNumber(Number(e.target.value))) {
      if (noZeroAfterDecimal(e.target.value)) {
        setMaxSite(e.target.value)
        setError(errorMessage)
      }
      const value = Number(parseFloat(e.target.value).toFixed('4'))
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          maxSite: e.target.value ? value : null
        }
      ).then((data) => {
        updateParameterList(data)
      })
    }
  }

  function handleChangeSource(e) {
    setSource(e.target.value)

    updateSourceTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        { sourceOfData: e.target.value }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeDerivation(e) {
    setDerivation(e)

    updateDerivationTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        { DerivationOfEstimateId: e.value }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function handleChangeNotes(e) {
    setNotes(e.target.value)

    updateNotesTimeOut(() => {
      requestUpdateSiteSpecieAssesmentParameter(
        siteSpecieParameterId,
        params.siteHistoryId,
        species.id,
        species.historyId,
        {
          notesOnSiteDataForSpecies: e.target.value
        }
      ).then((data) => {
        updateParameterList(data)
      })
    })
  }

  function shouldValidateField(fieldName) {
    if (qualified) {
      return checkFields[fieldName]
    }
    return true // or false based on your requirements when not qualified
  }

  function renderCriteriaOptions() {
    if (assessmentParameter.value == NUMBER_OF_MATURE_INDIVIDUALS_ID) {
      if (!isNaN(Number(siteBestGlobal)) && siteBestGlobal !== '') {
        return criteriaDefaultOptions.filter((criteria) => criteria.value !== D1b_Value)
      }
      return criteriaDefaultOptions
    }
    return criteriaDefaultNoD1Options
  }

const getIBACriteriaOptions = (isEuCountry) => {
  if (isEuCountry) {
    return ibaCriteriaDefaultOptions;
  }
  return ibaCriteriaDefaultOptions.filter(option => option.value !== 5);
}

  return (
    <SpeciesDetailsContainer noPadding={true}>
      <SpeciesDetailsItemWrapper>
        <Input
          value={minYear}
          onChange={handleChangeMinYear}
          placeholder="minYearPlaceholder"
          label="minYear"
          field="minYear"
          infoTooltip="minYearTooltip"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          error={
           yearError.field === 'minYear' ? yearError : shouldValidateField('earliestDataSetYear')
              ? error.field === 'minYear'
                ? error
                : {
                    field: '',
                    error: ''
                  }
              : { field: 'minYear', error: 'pleaseCompleteThisField' }
          }
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={maxYear}
          onChange={handleChangeMaxYear}
          placeholder="maxYearPlaceholder"
          label="maxYear"
          field="maxYear"
          infoTooltip="maxYearTooltip"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
          error={yearError.field === 'minYear' ? yearError : { field: '', error: '' }}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={minSite}
          onChange={handleChangeMinSite}
          onBlur={handleBlurMinSite}
          placeholder="enterSpeciesMinSiteData"
          label="minSite"
          field="minSite"
          infoTooltip="minSiteHint"
          zIndex="1070"
          isOptional={true}
          disabled={disabled}
          errorMarginTop="8px"
          error={error.field === 'minSite' ? error : { field: '', error: '' }}
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={bestSite}
          onChange={handleChangeBestSite}
          onBlur={handleBlurBestSite}
          placeholder="enterSpeciesBestSiteData"
          label="bestSite"
          field="bestSite"
          infoTooltip="bestSiteHint"
          zIndex="1070"
          isOptional={siteNatureOfOccurance}
          disabled={disabled}
          errorMarginTop="8px"
          error={
            shouldValidateField('bestSite')
              ? error.field === 'bestSite'
                ? error
                : {
                    field: '',
                    error: ''
                  }
              : { field: 'bestSite', error: 'pleaseCompleteThisField' }
          }
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={maxSite}
          onChange={handleChangeMaxSite}
          onBlur={handleBlurMaxSite}
          placeholder="enterSpeciesMaxSiteData"
          label="maxSite"
          field="maxSite"
          infoTooltip="maxSiteHint"
          zIndex="1070"
          disabled={disabled}
          isOptional={true}
          errorMarginTop="8px"
          error={error.field === 'maxSite' ? error : { field: '', error: '' }}
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={source}
          onChange={handleChangeSource}
          placeholder="enterSpeciesSiteSourceData"
          label="source"
          field="sourceOfData"
          infoTooltip="siteSourceHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          error={
            shouldValidateField('sourceOfData')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'sourceOfData', error: 'sourceOfData' }
          }
        />
      </div>
      <SpeciesDetailsItemWrapper>
        <Select
          value={derivation}
          onChange={handleChangeDerivation}
          options={formattedDerivationOptions}
          placeholder="derivationOfEstimate"
          label="derivation"
          field="DerivationOfEstimateId"
          infoTooltip="derivationHint"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          error={
            shouldValidateField('DerivationOfEstimateId')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'DerivationOfEstimateId', error: 'pleaseCompleteThisField' }
          }
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={evidence}
          onChange={handleChangeEvidence}
          placeholder="evidenceTheSpeciesIsPresentAtTheSite"
          label="evidence"
          field="evidenceThatSpeciesArePresentAtTheSite"
          infoTooltip="evidenceHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          error={
            shouldValidateField('speciesPresenceEvidence')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'evidenceThatSpeciesArePresentAtTheSite', error: 'pleaseCompleteThisField' }
          }
        />
      </div>
      <SpeciesDetailsItemWrapper>
        <Input
          value={yearRecent}
          onChange={handleChangeYearRecent}
          placeholder="yearForWitchThereIsEvidence"
          label="yearPresence"
          field="mostRecentYearForEvidenceOfPresence"
          infoTooltip="yearSpeciesDetailsHint"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          error={
            shouldValidateField('yearPresenceEvidence')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'mostRecentYearForEvidenceOfPresence', error: 'pleaseCompleteThisField' }
          }
        />
      </SpeciesDetailsItemWrapper>
      <SpeciesDetailsItemWrapper>
        <Input
          value={unitsNumber}
          onChange={handleChangeUnitsNumber}
          placeholder="minNumberOfReproductiveUnitsRuAtSite"
          label="unitsNumber"
          field="minNumberOfReproductiveUnits"
          infoTooltip="unitsNumberHint"
          zIndex="1070"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </SpeciesDetailsItemWrapper>

      <div style={{ width: '100%' }}>
        <Textarea
          value={sourceReproduc}
          onChange={handleChangeSourceReproduc}
          placeholder="sourceOfReproductiveUnitDataIncludingYear"
          label="sourceOfRuData"
          field="sourceOfReproductiveUnit"
          infoTooltip="speciesDetailsSourceHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </div>
      <div style={{ width: '100%' }}>
        <Textarea
          value={comprised}
          onChange={handleChangeComprised}
          placeholder="whatAre10RusComprisedOf"
          label="comprised"
          field="whatAre10RUcomprisedOf"
          infoTooltip="comprisedHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </div>
      <SpeciesDetailsItemWrapper>
        <MultiSelectionDropdown
          marginBottom="24px"
          marginTop="16px"
          label="natureOfOccurrence"
          field="natureOfOccurrence"
          secondStepOptions={renderCriteriaOptions()}
          value={natureOfOccurrence}
          saveChanges={handleChangeCriteria}
          infoTooltip="aggregationCriteriaHint"
          zIndex="1070"
          errorMarginTop="8px"
          error={
            shouldValidateField('natureOfOccurence')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'natureOfOccurrence', error: 'pleaseCompleteThisField' }
          }
        />
      </SpeciesDetailsItemWrapper>
      <div style={{ width: '100%' }}>
        <Textarea
          value={justification}
          onChange={handleChangeJustification}
          placeholder="justificationThatTheSpeciesIsAggregated"
          label="justification"
          field="justificationThatTheSpeciesAggregated"
          infoTooltip="largestAggregationHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={siteJustification}
          error={
            shouldValidateField('justification')
              ? {
                  field: '',
                  error: ''
                }
              : { field: 'justificationThatTheSpeciesAggregated', error: 'pleaseCompleteThisField' }
          }
        />
      </div>

      <div style={{ width: '100%' }}>
        <Textarea
          value={sourceHabitat}
          onChange={handleChangeSourceHabitat}
          placeholder="globallyMostImportant5OccupiedHabitat"
          label="source"
          field="sourceOfBeingGloballyMostImportant5PercentOccupiedHabitat"
          infoTooltip="sourceHabitatHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          errorMarginTop="8px"
          isOptional={true}
        />
      </div>

      {showBirdsDetails && (
        <FullWidthSpecieEstimateItemWrapper>
          <SpeciesDetailsIBAItemWrapper>
            <MultiC2C3SelectionDropdown
              dataTestId="ibaRelated"
              marginBottom="24px"
              marginTop="16px"
              label="ibaRelated"
              field="ibaRelated"
              onInputChange={handleIbaSelectionChange}
              secondStepOptions={
                site.isEuCountry && parameter?.SiteAssesmentParameterTypeId === NUMBER_OF_MATURE_INDIVIDUALS_ID
                  ? getIbaCriteriaEUOptions(currentIbaSelection)
                  : getIBACriteriaOptions(site.isEuCountry)
              }
              value={ibaConditions}
              saveChanges={handleChangeIbaCriteria}
              infoTooltip="ibaRelatedCriteriaHint"
              zIndex="1070"
              isOptional={true}
              errorMarginTop="8px"
              // disabled={disabled}
            />
          </SpeciesDetailsIBAItemWrapper>
        </FullWidthSpecieEstimateItemWrapper>
      )}

      <FullWidthSpecieEstimateItemWrapper>
        <Textarea
          value={notes}
          onChange={handleChangeNotes}
          placeholder="enterNotesOnSpeciesSiteData"
          label="noteLabel"
          infoTooltip="siteNoteHint"
          zIndex="1070"
          height="128px"
          disabled={disabled}
          isOptional={true}
        />
      </FullWidthSpecieEstimateItemWrapper>
    </SpeciesDetailsContainer>
  )
}

export default SpeciesParameterSiteSpeciesDataContent
